import React, { useState, useEffect } from 'react';
import QontakWebhookPanel from '../components/whatsapp/QontakWebhookPanel';
import { 
  Save, MessageSquare, Check, X, Send, RefreshCw, 
  AlertCircle, CheckCircle, Smartphone, Globe, Code, Plus, Trash2, Edit, Eye, EyeOff
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Mekari (Qontak) config block - manage OAuth credentials, channel picker,
 * template picker, and per-message-type template routing.
 */
const MekariConfigBlock = ({ config, setConfig, loadConfig }) => {
  const { token } = useAuth();
  const [channels, setChannels] = React.useState([]);
  const [templates, setTemplates] = React.useState([]);
  const [loadingChannels, setLoadingChannels] = React.useState(false);
  const [loadingTemplates, setLoadingTemplates] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [testPhone, setTestPhone] = React.useState('');
  const [testTemplateId, setTestTemplateId] = React.useState('');
  // Delta #41: track apakah Fetch Templates sudah pernah dieksekusi — supaya
  // UI bisa bedakan "belum pernah fetch" vs "sudah fetch tapi hasil 0 template".
  const [templatesFetched, setTemplatesFetched] = React.useState(false);
  // Delta #41: toggle tampilkan nilai credential tersimpan (fetch reveal=1 dari server)
  const [showCreds, setShowCreds] = React.useState(false);
  // Delta #42: input variabel untuk Quick Test Send (template Mekari butuh var sesuai body)
  const [testVars, setTestVars] = React.useState({});
  const toggleShowCreds = async () => {
    if (showCreds) {
      setConfig({ ...config, mekari_client_secret: '', mekari_access_token: '', mekari_refresh_token: '' });
      setShowCreds(false);
      return;
    }
    try {
      const r = await axios.get(`${API_URL}/api/v2/whatsapp/config?reveal=1`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.data?.success) {
        const d = r.data.data || {};
        setConfig({
          ...config,
          mekari_client_id: d.mekari_client_id || '',
          mekari_client_secret: d.mekari_client_secret || '',
          mekari_access_token: d.mekari_access_token || '',
          mekari_refresh_token: d.mekari_refresh_token || '',
        });
        setShowCreds(true);
        toast.success('Nilai credential tersimpan ditampilkan');
      } else {
        toast.error('Gagal memuat credential');
      }
    } catch (e) {
      toast.error(`Gagal memuat credential: ${e.response?.data?.detail || e.message}`);
    }
  };

  const notifTypes = [
    { key: 'registration', label: 'Konfirmasi Registrasi Pelanggan' },
    { key: 'invoice', label: 'Tagihan Bulanan' },
    { key: 'reminder', label: 'Pengingat Jatuh Tempo' },
    { key: 'payment_confirmation', label: 'Konfirmasi Pembayaran' },
    { key: 'isolir', label: 'Notif Isolir' },
    { key: 'voucher', label: 'Voucher Baru' },
    { key: 'document', label: 'Kirim Dokumen (PDF Invoice)' },
  ];

  const fetchChannels = async () => {
    try {
      setLoadingChannels(true);
      const r = await axios.get(`${API_URL}/api/whatsapp/mekari/channels`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.data?.success) {
        setChannels(r.data.data || []);
        toast.success(`Ditemukan ${r.data.data?.length || 0} channel Mekari`);
      } else {
        toast.error(`Gagal fetch channels: ${r.data?.error || 'unknown'}`);
      }
    } catch (e) {
      toast.error(`Fetch channels error: ${e.message}`);
    } finally {
      setLoadingChannels(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const r = await axios.get(`${API_URL}/api/whatsapp/mekari/templates?only_approved=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.data?.success) {
        setTemplates(r.data.data || []);
        setTemplatesFetched(true);
        if ((r.data.data || []).length === 0) {
          toast.warning('Belum ada template APPROVED di Mekari. Buat & submit template di dashboard Mekari terlebih dahulu.');
        } else {
          toast.success(`Ditemukan ${r.data.data.length} template APPROVED`);
        }
      } else {
        toast.error(`Gagal fetch templates: ${r.data?.error || 'unknown'}`);
      }
    } catch (e) {
      toast.error(`Fetch templates error: ${e.message}`);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Delta #43: auto-load channels & templates saat halaman dibuka,
  // supaya pilihan tersimpan langsung tampil tanpa klik Fetch manual.
  React.useEffect(() => {
    fetchChannels();
    fetchTemplates();
  }, []);

  const refreshToken = async () => {
    try {
      setRefreshing(true);
      const r = await axios.post(`${API_URL}/api/whatsapp/mekari/refresh-token`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.data?.success) {
        toast.success('Access token berhasil di-refresh');
        loadConfig && loadConfig();
      } else {
        toast.error(`Refresh gagal: ${r.data?.message || 'unknown'}`);
      }
    } catch (e) {
      toast.error(`Refresh error: ${e.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  const testSend = async () => {
    if (!testPhone) return toast.error('Isi nomor HP dulu');
    if (!testTemplateId && !config.mekari_default_template_id) return toast.error('Pilih template atau set default template');
    // Delta #42: pastikan semua variabel {{N}} terisi
    if (detectedVarCount > 0) {
      for (let i = 1; i <= detectedVarCount; i++) {
        if (!testVars[String(i)]) {
          return toast.error(`Isi dulu variabel {{${i}}} — template butuh ${detectedVarCount} variabel`);
        }
      }
    }
    try {
      setTesting(true);
      const r = await axios.post(
        `${API_URL}/api/whatsapp/mekari/test-send`,
        {
          phone_number: testPhone,
          template_id: testTemplateId || config.mekari_default_template_id,
          to_name: 'Admin Test',
          variables: testVars,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.data?.success) {
        toast.success(`Test message terkirim (id: ${(r.data.message_id || '').slice(0, 8)}...)`);
      } else {
        const detail = r.data?.error_detail?.error?.messages?.join(' | ') || r.data?.error || 'unknown';
        toast.error(`Send gagal: ${detail}`, { duration: 8000 });
      }
    } catch (e) {
      toast.error(`Send error: ${e.response?.data?.detail || e.message}`);
    } finally {
      setTesting(false);
    }
  };

  const updateTemplateMap = (key, value) => {
    setConfig({ ...config, mekari_template_map: { ...(config.mekari_template_map || {}), [key]: value } });
  };

  // Delta #42 & #47: hitung jumlah variabel {{N}} & generate data contoh otomatis
  const activeTestTemplate = templates.find((t) => t.id === (testTemplateId || config.mekari_default_template_id));
  const detectedVarCount = (() => {
    const body = activeTestTemplate?.body || '';
    const nums = new Set();
    const re = /\{\{\s*(\d+)\s*\}\}/g;
    let m;
    while ((m = re.exec(body)) !== null) nums.add(parseInt(m[1], 10));
    return nums.size ? Math.max(...nums) : 0;
  })();

  const generateSampleVars = (templateObj) => {
    if (!templateObj) return {};
    const body = templateObj.body || '';
    const name = (templateObj.name || '').toLowerCase();
    
    const today = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const todayStr = `${pad(today.getDate())}-${pad(today.getMonth() + 1)}-${today.getFullYear()}`;
    const nextMonth = new Date(today);
    nextMonth.setDate(nextMonth.getDate() + 30);
    const nextMonthStr = `${pad(nextMonth.getDate())}-${pad(nextMonth.getMonth() + 1)}-${nextMonth.getFullYear()}`;

    if (name.includes('pengingat') || body.includes('PENGINGAT PEMBAYARAN')) {
      return {
        '1': 'Budi Santoso',
        '2': 'INV-202611-0001',
        '3': '2610070001',
        '4': 'Paket Pelajar 20 Mbps',
        '5': 'Postpaid',
        '6': nextMonthStr,
        '7': 'Rp 100.000',
        '8': 'https://member.kumarahotspot.com/p/T06122503/login',
        '9': '08888200888',
      };
    }

    if (name.includes('perpanjang') || body.includes('informasi perpanjangan paket')) {
      return {
        '1': 'Budi Santoso',
        '2': 'INV-202611-0001',
        '3': '2610070001',
        '4': 'Paket Pelajar 20 Mbps',
        '5': 'Postpaid',
        '6': todayStr,
        '7': nextMonthStr,
        '8': 'Rp 100.000',
        '9': 'https://member.kumarahotspot.com/p/T06122503/login',
        '10': '08888 200 888',
      };
    }

    if (name.includes('tagihan') || (body.includes('Nama :') && body.includes('Jumlah Tagihan :'))) {
      return {
        '1': 'Budi Santoso',
        '2': '2610070001',
        '3': 'Rp 100.000',
      };
    }

    const baseSamples = {
      '1': 'Budi Santoso',
      '2': 'budi@gmail.com',
      '3': '2610070001',
      '4': 'Paket Pelajar 20 Mbps',
      '5': 'Postpaid',
      '6': todayStr,
      '7': nextMonthStr,
      '8': 'Rp 100.000',
      '9': '08888 200 888',
      '10': 'KUMARA HOTSPOT',
    };

    const re = /\{\{\s*(\d+)\s*\}\}/g;
    let m;
    const result = {};
    while ((m = re.exec(body)) !== null) {
      const idx = String(m[1]);
      result[idx] = baseSamples[idx] || `Sample ${idx}`;
    }
    return result;
  };

  React.useEffect(() => {
    if (activeTestTemplate && Object.keys(testVars).length === 0) {
      setTestVars(generateSampleVars(activeTestTemplate));
    }
  }, [activeTestTemplate?.id]);

  return (
    <div className="space-y-4 mt-4 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-lg border-2 border-emerald-200">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          <h4 className="font-semibold">Mekari (Qontak) Configuration</h4>
        </div>
        <span className="px-2 py-0.5 text-[10px] bg-emerald-600 text-white rounded-full font-bold">
          BSP Resmi Meta
        </span>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 dark:bg-blue-900/30">
        <p className="text-xs text-blue-800 dark:text-blue-200">
          <strong>ℹ Info:</strong> Semua pesan harus lewat template yang di-approve Meta.
          Buat template di dashboard Mekari (menu Campaigns → Template Message), submit ke Meta,
          setelah APPROVED baru bisa dipakai untuk kirim pesan.
        </p>
      </div>

      <div className="flex justify-end -mb-1">
        <Button
          type="button"
          size="sm"
          variant="outline"
          data-testid="mekari-toggle-show-creds"
          onClick={toggleShowCreds}
        >
          {showCreds ? <EyeOff className="w-3.5 h-3.5 mr-1" /> : <Eye className="w-3.5 h-3.5 mr-1" />}
          {showCreds ? 'Sembunyikan Nilai' : 'Tampilkan Nilai Tersimpan'}
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="font-semibold">Client ID</Label>
          <Input
            data-testid="mekari-client-id"
            autoComplete="off"
            readOnly
            onFocus={(e) => e.target.removeAttribute('readOnly')}
            placeholder="dari Mekari OAuth App"
            value={config.mekari_client_id || ''}
            onChange={(e) => setConfig({ ...config, mekari_client_id: e.target.value })}
          />
        </div>
        <div>
          <Label className="font-semibold">Client Secret</Label>
          <Input
            data-testid="mekari-client-secret"
            type={showCreds ? 'text' : 'password'}
            autoComplete="new-password"
            readOnly
            onFocus={(e) => e.target.removeAttribute('readOnly')}
            placeholder={config.mekari_client_secret_set ? '••••••• (tersimpan)' : 'dari Mekari OAuth App'}
            value={config.mekari_client_secret || ''}
            onChange={(e) => setConfig({ ...config, mekari_client_secret: e.target.value })}
          />
        </div>
        <div>
          <Label className="font-semibold">Access Token</Label>
          <Input
            data-testid="mekari-access-token"
            type={showCreds ? 'text' : 'password'}
            autoComplete="new-password"
            readOnly
            onFocus={(e) => e.target.removeAttribute('readOnly')}
            placeholder={config.mekari_access_token_set ? '••••••• (tersimpan)' : 'bt4FDdQB-...'}
            value={config.mekari_access_token || ''}
            onChange={(e) => setConfig({ ...config, mekari_access_token: e.target.value })}
          />
        </div>
        <div>
          <Label className="font-semibold">Refresh Token</Label>
          <Input
            data-testid="mekari-refresh-token"
            type={showCreds ? 'text' : 'password'}
            autoComplete="new-password"
            readOnly
            onFocus={(e) => e.target.removeAttribute('readOnly')}
            placeholder={config.mekari_refresh_token_set ? '••••••• (tersimpan)' : 'Z7fY5op...'}
            value={config.mekari_refresh_token || ''}
            onChange={(e) => setConfig({ ...config, mekari_refresh_token: e.target.value })}
          />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <Label className="font-semibold">Channel Integration ID</Label>
          <Button
            size="sm"
            variant="outline"
            data-testid="mekari-fetch-channels-btn"
            onClick={fetchChannels}
            disabled={loadingChannels}
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${loadingChannels ? 'animate-spin' : ''}`} />
            {loadingChannels ? 'Loading...' : 'Fetch Channels'}
          </Button>
        </div>
        <select
          data-testid="mekari-channel-select"
          className="w-full h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 text-sm"
          value={config.mekari_channel_integration_id || ''}
          onChange={(e) => setConfig({ ...config, mekari_channel_integration_id: e.target.value })}
        >
          <option value="">-- Pilih Channel (klik Fetch Channels dulu) --</option>
          {channels.map((c) => (
            <option key={c.id} value={c.id}>
              {c.account_name} • {c.phone_number} • {c.quality_score}
            </option>
          ))}
        </select>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <Label className="font-semibold">Templates APPROVED di Mekari</Label>
          <Button
            size="sm"
            variant="outline"
            data-testid="mekari-fetch-templates-btn"
            onClick={fetchTemplates}
            disabled={loadingTemplates}
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${loadingTemplates ? 'animate-spin' : ''}`} />
            {loadingTemplates ? 'Loading...' : 'Fetch Templates'}
          </Button>
        </div>
        {templates.length === 0 ? (
          templatesFetched ? (
            <div className="mt-2 p-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700">
              <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">⚠ 0 template APPROVED ditemukan di akun Mekari Anda</p>
              <p className="text-xs text-amber-700 dark:text-amber-200 mt-1">
                WhatsApp Business API wajib memakai template yang di-approve Meta. Buat template dulu di:
              </p>
              <ol className="text-xs text-amber-700 dark:text-amber-200 mt-1 ml-4 list-decimal space-y-0.5">
                <li>Login ke <a href="https://app.qontak.com" target="_blank" rel="noreferrer" className="underline font-medium">app.qontak.com</a></li>
                <li>Menu <b>Campaigns → Message Template</b> → <b>Create Message Template</b></li>
                <li>Isi body, tipe (MARKETING / UTILITY / AUTHENTICATION), lalu <b>Submit to Meta</b></li>
                <li>Tunggu status jadi <b>APPROVED</b> (biasanya &lt; 24 jam)</li>
                <li>Kembali ke sini, klik <b>Fetch Templates</b> lagi</li>
              </ol>
            </div>
          ) : (
            <p className="text-xs text-gray-500 italic mt-1">
              Belum ada template di-load. Klik "Fetch Templates" untuk muat dari Mekari.
            </p>
          )
        ) : (
          <p className="text-xs text-emerald-700 mt-1">{templates.length} template APPROVED tersedia untuk pilihan di bawah</p>
        )}
      </div>

      <div>
        <Label className="font-semibold">Default Template ID (fallback)</Label>
        <select
          data-testid="mekari-default-template-select"
          className="w-full h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 text-sm"
          value={config.mekari_default_template_id || ''}
          onChange={(e) => setConfig({ ...config, mekari_default_template_id: e.target.value })}
        >
          <option value="">-- Pilih Default Template --</option>
          {templates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} ({t.language}) • {t.category}
            </option>
          ))}
        </select>
      </div>

      <div>
        <Label className="font-semibold">Template Mapping per Jenis Notifikasi</Label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
          {notifTypes.map((n) => (
            <div key={n.key} className="flex items-center gap-2">
              <div className="text-xs w-40 shrink-0 text-gray-600 dark:text-gray-300">{n.label}</div>
              <select
                data-testid={`mekari-tpl-map-${n.key}`}
                className="flex-1 h-9 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 text-xs"
                value={(config.mekari_template_map || {})[n.key] || ''}
                onChange={(e) => updateTemplateMap(n.key, e.target.value)}
              >
                <option value="">(pakai default)</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </div>

      <QontakWebhookPanel config={config} loadConfig={loadConfig} />

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          data-testid="mekari-refresh-token-btn"
          onClick={refreshToken}
          disabled={refreshing}
        >
          <RefreshCw className={`w-3 h-3 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh Access Token'}
        </Button>
        {config.mekari_token_refreshed_at && (
          <span className="text-[10px] text-gray-500">
            Last refresh: {new Date(config.mekari_token_refreshed_at).toLocaleString('id-ID')}
          </span>
        )}
      </div>

      <div className="border-t border-emerald-200 pt-3 mt-2">
        <Label className="font-semibold text-xs">Quick Test Send</Label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
          <Input
            data-testid="mekari-test-phone"
            placeholder="Nomor HP (628xxx)"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
          />
          <select
            data-testid="mekari-test-template-select"
            className="h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 text-sm"
            value={testTemplateId}
            onChange={(e) => {
              const val = e.target.value;
              setTestTemplateId(val);
              const tpl = templates.find((t) => t.id === val) || templates.find((t) => t.id === config.mekari_default_template_id);
              if (tpl) setTestVars(generateSampleVars(tpl));
            }}
          >
            <option value="">-- Pakai Default Template --</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        {detectedVarCount > 0 && (
          <div className="mt-3 p-3 rounded-lg border border-emerald-300 bg-white/80 dark:bg-emerald-900/20 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2 pb-2 border-b border-emerald-200 dark:border-emerald-800/40">
              <p className="text-xs text-emerald-900 dark:text-emerald-100 font-medium">
                Template <b>{activeTestTemplate?.name || ''}</b> membutuhkan <b>{detectedVarCount} variabel</b>:
              </p>
              <div className="flex items-center gap-1.5">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  data-testid="mekari-auto-fill-vars-btn"
                  className="h-7 text-xs px-2.5 bg-emerald-100 dark:bg-emerald-800/50 border-emerald-300 text-emerald-800 dark:text-emerald-100 hover:bg-emerald-200 font-medium"
                  onClick={() => setTestVars(generateSampleVars(activeTestTemplate))}
                >
                  ⚡ Isi Otomatis Contoh Data
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs px-2 text-gray-500 hover:text-gray-700"
                  onClick={() => setTestVars({})}
                >
                  Kosongkan
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {Array.from({ length: detectedVarCount }, (_, i) => i + 1).map((n) => (
                <div key={n}>
                  <Label className="text-[10px]">Variabel {`{{${n}}}`}</Label>
                  <Input
                    data-testid={`mekari-test-var-${n}`}
                    placeholder={`Nilai untuk {{${n}}}`}
                    value={testVars[String(n)] || ''}
                    onChange={(e) => setTestVars({ ...testVars, [String(n)]: e.target.value })}
                  />
                </div>
              ))}
            </div>
            {activeTestTemplate?.body && (
              <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-2 whitespace-pre-wrap">
                <b>Preview body:</b> {activeTestTemplate.body}
              </p>
            )}
          </div>
        )}

        <Button
          size="sm"
          className="mt-2"
          data-testid="mekari-test-send-btn"
          onClick={testSend}
          disabled={testing}
        >
          <Send className="w-3 h-3 mr-1" />
          {testing ? 'Sending...' : 'Kirim Test'}
        </Button>
      </div>
    </div>
  );
};

const WhatsAppSettings = () => {
  const { token, user } = useAuth();
  const { selectedBranch } = useApp();
  const isKepalaCabang = ((user && user.role) || '').toLowerCase() === 'kepala_cabang';
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [instances, setInstances] = useState([]);
  const [newInstanceId, setNewInstanceId] = useState('');
  const [selectedInstanceId, setSelectedInstanceId] = useState(null);
  
  const [config, setConfig] = useState({
    provider: 'fonnte',
    is_active: false,
    fonnte_token: '',
    wablast_domain: '',
    wablast_token: '',
    wablast_secret_key: '',
    webjs_service_url: 'http://localhost:3001',
    business_api_access_token: '',
    business_api_phone_number_id: '',
    mekari_client_id: '',
    mekari_client_secret: '',
    mekari_access_token: '',
    mekari_refresh_token: '',
    mekari_channel_integration_id: '',
    mekari_default_template_id: '',
    mekari_default_language: 'id',
    mekari_template_map: {},
    hidden_providers: [],
    enable_invoice_notification: true,
    enable_payment_reminder: true,
    enable_payment_confirmation: true,
    enable_installation_notification: true,
    reminder_days: [7, 3, 1],
    message_batch_size: 5,
    delay_min_seconds: 180,
    delay_max_seconds: 300
  });

  const [testMessage, setTestMessage] = useState(() => {
    // Load from localStorage if available
    const saved = localStorage.getItem('whatsapp_test_message');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return { phone_number: '', message: '' };
      }
    }
    return { phone_number: '', message: '' };
  });

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  
  const [statistics, setStatistics] = useState(null);
  const [messages, setMessages] = useState([]);
  const [allMessages, setAllMessages] = useState([]); // Store all messages for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
  const [deleteAllConfirmText, setDeleteAllConfirmText] = useState('');
  const [messageFilter, setMessageFilter] = useState({
    status: '',
    message_type: '',
    limit: 10,
    sortBy: 'created_at',
    sortOrder: 'desc',
    search: ''
  });
  const waSearchTimer = React.useRef(null);
  const [bulkDateFrom, setBulkDateFrom] = useState('');
  const [bulkDateTo, setBulkDateTo] = useState('');

  // Save test message to localStorage whenever it changes
  useEffect(() => {
    if (testMessage.phone_number || testMessage.message) {
      localStorage.setItem('whatsapp_test_message', JSON.stringify(testMessage));
    }
  }, [testMessage]);

  useEffect(() => {
    fetchConfig();
    fetchStatus();
    fetchTemplates();
  }, []); // Run only once on mount

  // Track current tab for auto-load
  const [currentTab, setCurrentTab] = useState('provider');

  // Delta #70: kepala_cabang hanya boleh tab Riwayat
  useEffect(() => {
    if (isKepalaCabang && currentTab !== 'history') {
      setCurrentTab('history');
      fetchStatistics();
      fetchMessages();
    }
  }, [isKepalaCabang]);
  
  // Auto-load data when switching to history tab
  // Delta #67: refetch riwayat saat filter cabang global berubah
  useEffect(() => {
    if (currentTab === 'history') {
      fetchStatistics();
      fetchMessages();
    }
  }, [selectedBranch]);

  useEffect(() => {
    if (currentTab === 'history' && allMessages.length === 0) {
      fetchStatistics();
      fetchMessages();
    }
  }, [currentTab]);

  useEffect(() => {
    if (config.provider === 'whatsapp-webjs') {
      fetchInstances();
    }
  }, [config.provider]);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v2/whatsapp/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        // Backend returns *_token_set flags instead of actual tokens (for security)
        setConfig(prevConfig => {
          const newConfig = { ...prevConfig, ...response.data.data };
          
          // If backend says token is set, use placeholder to indicate it's saved
          if (response.data.data.wablast_token_set && !prevConfig.wablast_token) {
            newConfig.wablast_token = '***TOKEN_SET***'; // Placeholder
          }
          if (response.data.data.wablast_secret_key_set && !prevConfig.wablast_secret_key) {
            newConfig.wablast_secret_key = '***SECRET_KEY_SET***'; // Placeholder
          }
          if (response.data.data.fonnte_token_set && !prevConfig.fonnte_token) {
            newConfig.fonnte_token = '***TOKEN_SET***'; // Placeholder
          }
          
          // If token fields exist in prevConfig and not placeholder, keep them
          if (prevConfig.wablast_token && prevConfig.wablast_token !== '***TOKEN_SET***') {
            newConfig.wablast_token = prevConfig.wablast_token;
          }
          if (prevConfig.wablast_secret_key && prevConfig.wablast_secret_key !== '***SECRET_KEY_SET***') {
            newConfig.wablast_secret_key = prevConfig.wablast_secret_key;
          }
          if (prevConfig.fonnte_token && prevConfig.fonnte_token !== '***TOKEN_SET***') {
            newConfig.fonnte_token = prevConfig.fonnte_token;
          }
          
          return newConfig;
        });
      }
    } catch (error) {
      console.error('Error fetching WhatsApp config:', error);
    }
  };

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v2/whatsapp/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setStatus(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching status:', error);
    }
  };

  const fetchInstances = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/whatsapp/instances`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setInstances(response.data.instances);
      }
    } catch (error) {
      console.error('Error fetching instances:', error);
    }
  };

  const createInstance = async () => {
    if (!newInstanceId) {
      toast.error('Instance ID harus diisi');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/whatsapp/instances?instance_id=${newInstanceId}&priority=${instances.length + 1}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Instance baru berhasil dibuat');
        setNewInstanceId('');
        fetchInstances();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal membuat instance');
    } finally {
      setLoading(false);
    }
  };

  const deleteInstance = async (instanceId) => {
    if (!confirm(`Hapus instance ${instanceId}?`)) return;

    setLoading(true);
    try {
      const response = await axios.delete(
        `${API_URL}/api/whatsapp/instances/${instanceId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Instance berhasil dihapus');
        fetchInstances();
        fetchStatus();
      }
    } catch (error) {
      toast.error('Gagal menghapus instance');
    } finally {
      setLoading(false);
    }
  };

  const activateInstance = async (instanceId) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/whatsapp/instances/${instanceId}/activate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`Instance ${instanceId} sekarang aktif`);
        fetchStatus();
        fetchInstances();
      }
    } catch (error) {
      toast.error('Gagal mengaktifkan instance');
    } finally {
      setLoading(false);
    }
  };

  const fetchInstanceQR = async (instanceId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/whatsapp/instances/${instanceId}/qr`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success && response.data.qr) {
        setQrCode(response.data.qr);
        setSelectedInstanceId(instanceId);
      } else {
        toast.info('QR code belum tersedia untuk instance ini');
      }
    } catch (error) {
      toast.error('Gagal mengambil QR code');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'bg-green-500';
      case 'qr_required': return 'bg-yellow-500';
      case 'blocked': return 'bg-red-500';
      case 'disconnected': return 'bg-gray-500';
      default: return 'bg-gray-400';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'connected': return 'Connected';
      case 'qr_required': return 'QR Required';
      case 'blocked': return 'Blocked';
      case 'disconnected': return 'Disconnected';
      case 'connecting': return 'Connecting...';
      default: return status;
    }
  };

  const fetchQRCode = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/whatsapp/qr`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success && response.data.qr) {
        setQrCode(response.data.qr);
        toast.success('QR Code berhasil dimuat');
      } else {
        toast.info(response.data.message || 'QR code belum tersedia, tunggu sebentar dan coba lagi');
      }
    } catch (error) {
      toast.error('Gagal mengambil QR code: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    setLoading(true);
    try {
      // Remove placeholder values before sending to backend
      const configToSend = { ...config };
      if (configToSend.wablast_token === '***TOKEN_SET***') {
        delete configToSend.wablast_token; // Don't send placeholder
      }
      if (configToSend.wablast_secret_key === '***SECRET_KEY_SET***') {
        delete configToSend.wablast_secret_key; // Don't send placeholder
      }
      if (configToSend.fonnte_token === '***TOKEN_SET***') {
        delete configToSend.fonnte_token; // Don't send placeholder
      }
      
      const response = await axios.post(
        `${API_URL}/api/v2/whatsapp/config`,
        configToSend,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Konfigurasi WhatsApp berhasil disimpan');
        fetchStatus();
        fetchConfig(); // Refresh config to get updated token_set flags
      }
    } catch (error) {
      toast.error('Gagal menyimpan konfigurasi');
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestMessage = async () => {
    if (!testMessage.phone_number || !testMessage.message) {
      toast.error('Nomor telepon dan pesan harus diisi');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/whatsapp/send-test?phone_number=${testMessage.phone_number}&message=${encodeURIComponent(testMessage.message)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Pesan test berhasil dikirim!');
        // Don't clear fields - keep them for easy resend
      } else {
        toast.error(response.data.error || 'Gagal mengirim pesan');
      }
    } catch (error) {
      toast.error('Gagal mengirim pesan test');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v2/whatsapp/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setTemplates(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleSaveTemplates = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/v2/whatsapp/templates`,
        templates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Template berhasil disimpan');
        fetchTemplates();
      }
    } catch (error) {
      toast.error('Gagal menyimpan template');
    } finally {
      setLoading(false);
    }
  };

  const handleResetTemplates = async () => {
    if (!confirm('Apakah Anda yakin ingin reset semua template ke default?')) return;

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/v2/whatsapp/templates/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Template berhasil direset');
        fetchTemplates();
      }
    } catch (error) {
      toast.error('Gagal reset template');
    } finally {
      setLoading(false);
    }
  };

  const updateTemplateContent = (templateId, newContent) => {
    setTemplates(templates.map(t => 
      t.id === templateId ? { ...t, template_content: newContent } : t
    ));
  };

  const handlePreviewTemplate = (template) => {
    setPreviewTemplate(template);
    setShowPreviewDialog(true);
  };

  const renderPreviewWithSampleData = (content, templateType) => {
    // Sample data based on template type - matching backend variables
    const sampleData = {
      registration: {
        '{customer_name}': 'ZAHRA',
        '{customer_email}': 'zahra@example.com',
        '{customer_id}': '25120500002',
        '{package_name}': 'SILVER 150',
        '{payment_type}': 'Prepaid',
        '{activation_date}': '2025-12-05',
        '{due_date}': '2026-01-05',
        '{total_bill}': '150.000'
      },
      invoice: {
        '{customer_name}': 'SUSIYANTO',
        '{invoice_id}': 'INV-25051200002',
        '{amount}': '150.000',
        '{due_date}': '2025-12-12'
      },
      reminder: {
        '{customer_name}': 'ZAHRA',
        '{invoice_id}': 'INV-25051200003',
        '{amount}': '150.000',
        '{emoji}': '⚠️',
        '{urgency}': 'akan jatuh tempo dalam 3 hari',
        '{days_left}': '3'
      },
      payment_confirmation: {
        '{customer_name}': 'SUSIYANTO',
        '{invoice_id}': 'INV-25051200002',
        '{amount}': '150.000',
        '{payment_date}': '2025-12-05'
      },
      owner_deposit: {
        '{company_name}': 'Nama Perusahaan',
        '{owner_name}': 'BUDIMAN',
        '{deposit_amount}': '1.000.000',
        '{admin_name}': 'Admin Pusat',
        '{deposit_date}': '07 Des 2024 18:30',
        '{deposit_notes}': 'Top up bulanan Desember',
        '{new_balance}': '5.000.000'
      }
    };

    const data = sampleData[templateType] || sampleData.registration;
    let preview = content;
    
    // Replace all variables with sample data
    Object.keys(data).forEach(key => {
      preview = preview.replaceAll(key, data[key]);
    });

    return preview;
  };

  const fetchStatistics = async () => {
    try {
      const statParams = new URLSearchParams();
      if (selectedBranch && selectedBranch !== 'all') statParams.append('branch_id', selectedBranch);
      const response = await axios.get(`${API_URL}/api/whatsapp/statistics?${statParams.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const fetchMessages = async (filterOverride = null) => {
    try {
      const mf = filterOverride || messageFilter;
      const params = new URLSearchParams();
      if (mf.status) params.append('status', mf.status);
      if (mf.message_type) params.append('message_type', mf.message_type);
      if (mf.search) params.append('search', mf.search);
      if (selectedBranch && selectedBranch !== 'all') params.append('branch_id', selectedBranch);
      // Fetch more data for pagination (limit * 10)
      params.append('limit', mf.limit * 10);
      
      const response = await axios.get(`${API_URL}/api/whatsapp/messages?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        let sortedMessages = [...response.data.data];
        
        // Client-side sorting
        if (mf.sortBy) {
          sortedMessages.sort((a, b) => {
            let aVal = a[mf.sortBy];
            let bVal = b[mf.sortBy];
            
            // Handle date sorting
            if (mf.sortBy === 'created_at') {
              aVal = new Date(aVal);
              bVal = new Date(bVal);
            }
            
            if (mf.sortOrder === 'asc') {
              return aVal > bVal ? 1 : -1;
            } else {
              return aVal < bVal ? 1 : -1;
            }
          });
        }
        
        setAllMessages(sortedMessages);
        setCurrentPage(1); // Reset to page 1
        
        // Set paginated messages
        const startIndex = 0;
        const endIndex = mf.limit;
        setMessages(sortedMessages.slice(startIndex, endIndex));
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };


  const handleDeleteAllMessages = async () => {
    try {
      if (deleteAllConfirmText !== 'HAPUS SEMUA') {
        toast.error('Ketik "HAPUS SEMUA" untuk konfirmasi');
        return;
      }

      const response = await axios.delete(`${API_URL}/api/whatsapp/messages/delete-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setShowDeleteAllDialog(false);
        setDeleteAllConfirmText('');
        fetchMessages();
        fetchStatistics();
      }
    } catch (error) {
      toast.error('Gagal menghapus semua pesan');
      console.error(error);
    }
  };


  // Pagination handler
  const handlePageChange = (newPage) => {
    const startIndex = (newPage - 1) * messageFilter.limit;
    const endIndex = startIndex + messageFilter.limit;
    setMessages(allMessages.slice(startIndex, endIndex));
    setCurrentPage(newPage);
  };

  // Calculate pagination info
  const totalPages = Math.ceil(allMessages.length / messageFilter.limit);
  const startEntry = allMessages.length > 0 ? (currentPage - 1) * messageFilter.limit + 1 : 0;
  const endEntry = Math.min(currentPage * messageFilter.limit, allMessages.length);
  const totalEntries = allMessages.length;

  const resendMessage = async (messageId) => {
    if (!confirm('Kirim ulang pesan ini ke pelanggan? (akan memakai kuota WhatsApp)')) return;
    try {
      const response = await axios.post(
        `${API_URL}/api/whatsapp/messages/${messageId}/resend`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.data?.status === 'sent') {
        toast.success('Pesan berhasil dikirim ulang');
      } else {
        toast.error(`Kirim ulang gagal: ${response.data?.data?.error || 'unknown'}`);
      }
      fetchMessages();
      fetchStatistics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengirim ulang pesan');
    }
  };

  const bulkDeleteRange = async () => {
    if (!bulkDateFrom || !bulkDateTo) {
      toast.error('Pilih tanggal Dari dan Sampai dulu');
      return;
    }
    if (bulkDateFrom > bulkDateTo) {
      toast.error('Tanggal Dari tidak boleh melebihi tanggal Sampai');
      return;
    }
    if (!confirm(`Hapus SEMUA pesan dari ${bulkDateFrom} sampai ${bulkDateTo}? (filter cabang/status/tipe yang aktif ikut diterapkan)`)) return;
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/whatsapp/messages/bulk-delete`,
        {
          date_from: bulkDateFrom,
          date_to: bulkDateTo,
          status: messageFilter.status,
          message_type: messageFilter.message_type,
          branch_id: selectedBranch !== 'all' ? selectedBranch : ''
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        const _delCount = response.data?.data?.deleted_count ?? 0;
        if (_delCount > 0) {
          toast.success(`${_delCount} pesan berhasil dihapus`);
        } else {
          toast.info(`Tidak ada pesan pada rentang ${bulkDateFrom} s/d ${bulkDateTo} yang cocok dengan filter aktif`);
        }
        fetchMessages();
        fetchStatistics();
      }
    } catch (error) {
      toast.error('Gagal menghapus pesan');
    } finally {
      setLoading(false);
    }
  };

  const deleteMessage = async (messageId) => {
    if (!confirm('Apakah Anda yakin ingin menghapus pesan ini?')) return;
    
    try {
      const response = await axios.delete(`${API_URL}/api/whatsapp/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('Pesan berhasil dihapus');
        fetchMessages();
        fetchStatistics();
      }
    } catch (error) {
      toast.error('Gagal menghapus pesan');
    }
  };

  const bulkDeleteMessages = async (olderThanDays) => {
    if (!confirm(`Apakah Anda yakin ingin menghapus semua pesan lebih dari ${olderThanDays} hari?`)) return;
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/whatsapp/messages/bulk-delete`,
        { 
          older_than_days: olderThanDays,
          status: messageFilter.status,
          message_type: messageFilter.message_type,
          branch_id: selectedBranch !== 'all' ? selectedBranch : ''
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const _delCount = response.data?.data?.deleted_count ?? response.data?.deleted_count ?? 0;
        if (_delCount > 0) {
          toast.success(`${_delCount} pesan berhasil dihapus`);
        } else {
          toast.info(`Tidak ada pesan yang dihapus — tidak ada pesan lebih dari ${olderThanDays} hari yang cocok dengan filter aktif`);
        }
        fetchMessages();
        fetchStatistics();
      }
    } catch (error) {
      toast.error('Gagal menghapus pesan');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            WhatsApp Settings
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Konfigurasi notifikasi WhatsApp untuk pelanggan
          </p>
        </div>
        
        {status && (
          <Badge variant={status.connected ? "default" : "destructive"}>
            {status.connected ? (
              <><CheckCircle className="w-4 h-4 mr-1" /> Connected</>
            ) : (
              <><X className="w-4 h-4 mr-1" /> Disconnected</>
            )}
          </Badge>
        )}
      </div>

      <Tabs defaultValue={isKepalaCabang ? 'history' : 'provider'} value={isKepalaCabang ? 'history' : undefined} className="space-y-6" onValueChange={(v) => { setCurrentTab(v); if (v === 'history') { fetchStatistics(); fetchMessages(); } }}>
        <TabsList className={`grid w-full ${isKepalaCabang ? 'grid-cols-1' : 'grid-cols-6'}`}>
          {!isKepalaCabang && (<>
          <TabsTrigger value="provider">
            <Smartphone className="w-4 h-4 mr-2" />
            Provider
          </TabsTrigger>
          <TabsTrigger value="instances" disabled={config.provider !== 'whatsapp-webjs'}>
            <Code className="w-4 h-4 mr-2" />
            Multi-Instance
          </TabsTrigger>
          <TabsTrigger value="notifications">
            <MessageSquare className="w-4 h-4 mr-2" />
            Notifikasi
          </TabsTrigger>
          <TabsTrigger value="templates">
            <MessageSquare className="w-4 h-4 mr-2" />
            Template
          </TabsTrigger>
          </>)}
          <TabsTrigger value="history">
            <MessageSquare className="w-4 h-4 mr-2" />
            Riwayat
          </TabsTrigger>
          {!isKepalaCabang && (
          <TabsTrigger value="test">
            <Send className="w-4 h-4 mr-2" />
            Test
          </TabsTrigger>
          )}
        </TabsList>

        {/* PROVIDER CONFIGURATION */}
        <TabsContent value="provider" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Pilih WhatsApp Provider</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Provider Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Mekari (Qontak) - BSP resmi, primary provider */}
                <div
                  data-testid="provider-card-mekari"
                  onClick={() => setConfig({ ...config, provider: 'mekari-qontak' })}
                  className={`p-4 border-2 rounded-lg transition-all cursor-pointer relative ${
                    config.provider === 'mekari-qontak'
                      ? 'border-emerald-600 bg-emerald-50 dark:bg-emerald-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-emerald-400'
                  }`}
                >
                  <span className="absolute top-2 right-2 px-2 py-0.5 text-[10px] bg-emerald-600 text-white rounded-full font-bold">
                    RECOMMENDED
                  </span>
                  <div className="flex items-center justify-center mb-2">
                    <CheckCircle className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <h3 className="font-semibold text-center">Mekari (Qontak)</h3>
                  <p className="text-xs text-center text-gray-500 mt-1 dark:text-slate-400">
                    BSP Resmi Indonesia • Meta Verified
                  </p>
                  {config.provider === 'mekari-qontak' && (
                    <div className="mt-2 text-center text-xs font-bold text-emerald-600 dark:text-emerald-400">
                      ✓ Selected
                    </div>
                  )}
                </div>

                {!(config.hidden_providers || []).includes('fonnte') && (
                <div
                  onClick={() => setConfig({ ...config, provider: 'fonnte' })}
                  className={`p-4 border-2 rounded-lg transition-all cursor-pointer ${
                    config.provider === 'fonnte'
                      ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-indigo-400'
                  }`}
                >
                  <Globe className="w-8 h-8 mb-2 mx-auto text-indigo-600 dark:text-indigo-400" />
                  <h3 className="font-semibold text-center">Fonnte</h3>
                  <p className="text-xs text-center text-gray-500 mt-1 dark:text-slate-400">
                    API Gateway Indonesia
                  </p>
                </div>
                )}

                {!(config.hidden_providers || []).includes('wablast') && (
                <div
                  onClick={() => setConfig({ ...config, provider: 'wablast' })}
                  className={`p-4 border-2 rounded-lg transition-all cursor-pointer ${
                    config.provider === 'wablast'
                      ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-indigo-400'
                  }`}
                >
                  <MessageSquare className="w-8 h-8 mb-2 mx-auto text-green-600 dark:text-green-400" />
                  <h3 className="font-semibold text-center">Wablast</h3>
                  <p className="text-xs text-center text-gray-500 mt-1 dark:text-slate-400">
                    Wablas WhatsApp Gateway
                  </p>
                </div>
                )}

                {!(config.hidden_providers || []).includes('whatsapp-webjs') && (
                <div
                  data-provider="whatsapp-webjs"
                  onClick={() => {
                    console.log('Setting provider to whatsapp-webjs');
                    setConfig({ ...config, provider: 'whatsapp-webjs' });
                  }}
                  className={`p-4 border-2 rounded-lg transition-all cursor-pointer ${
                    config.provider === 'whatsapp-webjs'
                      ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-indigo-400'
                  }`}
                >
                  <Code className="w-8 h-8 mb-2 mx-auto text-blue-600 dark:text-blue-400" />
                  <h3 className="font-semibold text-center">WhatsApp-Web.js</h3>
                  <p className="text-xs text-center text-gray-500 mt-1 dark:text-slate-400">
                    Self-hosted (Gratis)
                  </p>
                  {config.provider === 'whatsapp-webjs' && (
                    <div className="mt-2 text-center text-xs font-bold text-indigo-600 dark:text-indigo-400">
                      ✓ Selected
                    </div>
                  )}
                </div>
                )}

                {!(config.hidden_providers || []).includes('whatsapp-business-api') && (
                <div
                  onClick={() => setConfig({ ...config, provider: 'whatsapp-business-api' })}
                  className={`p-4 border-2 rounded-lg transition-all cursor-pointer ${
                    config.provider === 'whatsapp-business-api'
                      ? 'border-green-600 bg-green-50 dark:bg-green-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-green-400'
                  }`}
                >
                  <div className="flex items-center justify-center mb-2">
                    <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="font-semibold text-center">WhatsApp Business API</h3>
                  <p className="text-xs text-center text-gray-500 mt-1 dark:text-slate-400">
                    Official Meta API ✅ Centang Biru
                  </p>
                  {config.provider === 'whatsapp-business-api' && (
                    <div className="mt-2 text-center text-xs font-bold text-green-600 dark:text-green-400">
                      ✓ Selected
                    </div>
                  )}
                  <div className="mt-2 text-center">
                    <span className="inline-block px-2 py-1 text-[10px] bg-green-100 text-green-700 rounded-full font-semibold dark:bg-green-900/40 dark:text-green-300">
                      ANTI BLOK
                    </span>
                  </div>
                </div>
                )}
              </div>

              {/* Provider-specific configuration */}
              {config.provider === 'fonnte' && (
                <div className="space-y-4 mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <h4 className="font-semibold">Fonnte Configuration</h4>
                  <div>
                    <Label>API Token</Label>
                    <Input
                      type="password"
                      placeholder="Masukkan Fonnte API Token"
                      value={config.fonnte_token}
                      onChange={(e) => setConfig({ ...config, fonnte_token: e.target.value })}
                    />
                    <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                      Dapatkan token dari dashboard Fonnte Anda
                    </p>
                  </div>
                </div>
              )}

              {config.provider === 'wablast' && (
                <div className="space-y-4 mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <h4 className="font-semibold">Wablast Configuration</h4>
                  <div>
                    <Label>Domain</Label>
                    <Input
                      placeholder="https://kudus.wablas.com"
                      value={config.wablast_domain}
                      onChange={(e) => setConfig({ ...config, wablast_domain: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>API Keys / Token</Label>
                    <Input
                      type={config.wablast_token === '***TOKEN_SET***' ? 'text' : 'password'}
                      placeholder="Masukkan Wablast Token (API Keys)"
                      value={config.wablast_token === '***TOKEN_SET***' ? '' : (config.wablast_token || '')}
                      onChange={(e) => setConfig({ ...config, wablast_token: e.target.value })}
                      disabled={config.wablast_token === '***TOKEN_SET***'}
                      className={config.wablast_token === '***TOKEN_SET***' ? 'bg-gray-100' : ''}
                    />
                    <p className="text-xs mt-1">
                      {config.wablast_token === '***TOKEN_SET***' ? (
                        <span className="text-green-600 dark:text-green-400">✅ Token sudah tersimpan di server (klik "Clear" untuk mengubah)</span>
                      ) : config.wablast_token ? (
                        <span className="text-blue-600 dark:text-blue-400">Token akan disimpan saat klik "Simpan Konfigurasi"</span>
                      ) : (
                        <span className="text-gray-500 dark:text-slate-400">Token panjang dari dashboard Wablas</span>
                      )}
                    </p>
                    {config.wablast_token === '***TOKEN_SET***' && (
                      <button
                        type="button"
                        onClick={() => setConfig({ ...config, wablast_token: '' })}
                        className="mt-1 text-xs text-red-600 hover:text-red-800 dark:text-red-400"
                      >
                        Clear & Ubah Token
                      </button>
                    )}
                  </div>
                  <div>
                    <Label>Secret Key</Label>
                    <Input
                      type={config.wablast_secret_key === '***SECRET_KEY_SET***' ? 'text' : 'password'}
                      placeholder="Masukkan Wablast Secret Key"
                      value={config.wablast_secret_key === '***SECRET_KEY_SET***' ? '' : (config.wablast_secret_key || '')}
                      onChange={(e) => setConfig({ ...config, wablast_secret_key: e.target.value })}
                      disabled={config.wablast_secret_key === '***SECRET_KEY_SET***'}
                      className={config.wablast_secret_key === '***SECRET_KEY_SET***' ? 'bg-gray-100' : ''}
                    />
                    <p className="text-xs mt-1">
                      {config.wablast_secret_key === '***SECRET_KEY_SET***' ? (
                        <span className="text-green-600 dark:text-green-400">✅ Secret Key sudah tersimpan di server (klik "Clear" untuk mengubah)</span>
                      ) : config.wablast_secret_key ? (
                        <span className="text-blue-600 dark:text-blue-400">Secret Key akan disimpan saat klik "Simpan Konfigurasi"</span>
                      ) : (
                        <span className="text-gray-500 dark:text-slate-400">Klik 'Show here' di dashboard Wablas untuk melihat Secret Key</span>
                      )}
                    </p>
                    {config.wablast_secret_key === '***SECRET_KEY_SET***' && (
                      <button
                        type="button"
                        onClick={() => setConfig({ ...config, wablast_secret_key: '' })}
                        className="mt-1 text-xs text-red-600 hover:text-red-800 dark:text-red-400"
                      >
                        Clear & Ubah Secret Key
                      </button>
                    )}
                  </div>
                </div>
              )}

              {config.provider === 'whatsapp-webjs' && (
                <div className="space-y-4 mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <h4 className="font-semibold">WhatsApp-Web.js Configuration</h4>
                  <p className="text-xs text-green-600 dark:text-green-400">✅ Provider: {config.provider}</p>
                  <div>
                    <Label>Service URL</Label>
                    <Input
                      placeholder="http://localhost:3001"
                      value={config.webjs_service_url}
                      onChange={(e) => setConfig({ ...config, webjs_service_url: e.target.value })}
                    />
                    <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                      Node.js service harus running terlebih dahulu
                    </p>
                  </div>
                  
                  <div>
                    <Button 
                      onClick={fetchQRCode}
                      variant="outline"
                      className="w-full"
                      type="button"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Get QR Code
                    </Button>
                    
                    {qrCode && (
                      <div className="mt-4 p-4 bg-white dark:bg-gray-900 rounded-lg text-center border">
                        <h5 className="font-semibold mb-2">Scan QR Code</h5>
                        <img 
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(qrCode)}`}
                          alt="WhatsApp QR Code"
                          className="mx-auto"
                        />
                        <p className="text-xs text-gray-500 mt-2 dark:text-slate-400">
                          1. Buka WhatsApp di HP<br/>
                          2. Klik Menu (3 titik) → Linked Devices<br/>
                          3. Klik "Link a Device"<br/>
                          4. Scan QR code di atas
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {config.provider === 'whatsapp-business-api' && (
                <div className="space-y-4 mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border-2 border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                    <h4 className="font-semibold">WhatsApp Business API Official Configuration</h4>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 dark:bg-blue-900/30">
                    <p className="text-xs text-blue-800">
                      <strong>ℹ️ Info:</strong> WhatsApp Business API Official dari Meta dengan centang biru verified. 
                      Anti block, professional, dan mendukung semua fitur Business API.
                    </p>
                  </div>

                  <div>
                    <Label className="font-semibold">Access Token</Label>
                    <Input
                      type="password"
                      placeholder="EAAG... (dari Meta Business Manager)"
                      value={config.business_api_access_token}
                      onChange={(e) => setConfig({ ...config, business_api_access_token: e.target.value })}
                    />
                    <p className="text-xs text-gray-600 mt-1 dark:text-slate-300">
                      Dapatkan dari Meta Business Manager → App Dashboard → WhatsApp → Configuration
                    </p>
                  </div>

                  <div>
                    <Label className="font-semibold">Phone Number ID</Label>
                    <Input
                      placeholder="1234567890 (Business Phone Number ID)"
                      value={config.business_api_phone_number_id}
                      onChange={(e) => setConfig({ ...config, business_api_phone_number_id: e.target.value })}
                    />
                    <p className="text-xs text-gray-600 mt-1 dark:text-slate-300">
                      Phone Number ID dari WhatsApp Business Account Anda
                    </p>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 dark:bg-amber-900/30">
                    <p className="text-xs text-amber-800">
                      <strong>📋 Langkah Setup:</strong><br/>
                      1. Daftar di <a href="https://business.facebook.com" target="_blank" rel="noopener" className="underline font-semibold">Meta Business Manager</a><br/>
                      2. Buat WhatsApp Business Account<br/>
                      3. Verifikasi nomor WhatsApp Business<br/>
                      4. Dapatkan Access Token & Phone Number ID<br/>
                      5. Masukkan credentials di atas dan simpan
                    </p>
                  </div>

                  <div className="flex items-center gap-2 p-3 bg-green-100 border border-green-300 rounded-lg dark:bg-green-900/40">
                    <CheckCircle className="w-5 h-5 text-green-600 shrink-0 dark:text-green-400" />
                    <p className="text-xs text-green-800 font-medium">
                      ✅ Centang Biru Verified | Anti Block | Support 1000+ pesan/detik
                    </p>
                  </div>
                </div>
              )}

              {config.provider === 'mekari-qontak' && (
                <MekariConfigBlock
                  config={config}
                  setConfig={setConfig}
                  loadConfig={fetchConfig}
                />
              )}

              {/* Active Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div>
                  <h4 className="font-semibold">Aktifkan WhatsApp Notifications</h4>
                  <p className="text-sm text-gray-500 dark:text-slate-400">
                    Enable/disable semua notifikasi WhatsApp
                  </p>
                </div>
                <button
                  onClick={() => setConfig({ ...config, is_active: !config.is_active })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    config.is_active ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      config.is_active ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* MULTI-INSTANCE MANAGEMENT */}
        <TabsContent value="instances" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Manage WhatsApp Instances</CardTitle>
              <p className="text-sm text-gray-500 dark:text-slate-400">
                Setup multiple WhatsApp accounts dengan auto-failover
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add New Instance */}
              <div className="p-4 border rounded-lg bg-gray-50 dark:bg-gray-800">
                <h4 className="font-semibold mb-3">Tambah Instance Baru</h4>
                <div className="flex gap-2">
                  <Input
                    placeholder="Instance ID (contoh: wa-instance-2)"
                    value={newInstanceId}
                    onChange={(e) => setNewInstanceId(e.target.value)}
                  />
                  <Button onClick={createInstance} disabled={loading}>
                    <Plus className="w-4 h-4 mr-2" />
                    Tambah
                  </Button>
                </div>
              </div>

              {/* Instances List */}
              <div className="space-y-3">
                <h4 className="font-semibold">Active Instances</h4>
                {instances.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-slate-400">Belum ada instance</p>
                ) : (
                  instances.map((instance) => (
                    <div
                      key={instance.id}
                      className="p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${getStatusColor(instance.status)}`} />
                            <div>
                              <h5 className="font-semibold">{instance.id}</h5>
                              <p className="text-sm text-gray-500 dark:text-slate-400">
                                Priority: {instance.priority} | Status: {getStatusLabel(instance.status)}
                              </p>
                              {instance.phoneNumber && (
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  📱 {instance.phoneNumber}
                                </p>
                              )}
                            </div>
                          </div>
                          {instance.lastError && (
                            <p className="text-xs text-red-500 mt-2">
                              Error: {instance.lastError}
                            </p>
                          )}
                        </div>

                        <div className="flex gap-2">
                          {instance.status === 'qr_required' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => fetchInstanceQR(instance.id)}
                            >
                              <Code className="w-4 h-4 mr-1" />
                              QR
                            </Button>
                          )}
                          {instance.status === 'connected' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => activateInstance(instance.id)}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Aktifkan
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteInstance(instance.id)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* QR Code Display */}
              {qrCode && selectedInstanceId && (
                <div className="mt-4 p-4 bg-white dark:bg-gray-900 rounded-lg border">
                  <h4 className="font-semibold mb-2 text-center">
                    QR Code untuk {selectedInstanceId}
                  </h4>
                  <div className="flex justify-center">
                    <img 
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrCode)}`}
                      alt="WhatsApp QR Code"
                      className="mx-auto"
                    />
                  </div>
                  <p className="text-xs text-center text-gray-500 mt-2 dark:text-slate-400">
                    Scan dengan WhatsApp di ponsel Anda
                  </p>
                  <Button
                    onClick={() => {
                      setQrCode(null);
                      setSelectedInstanceId(null);
                    }}
                    variant="outline"
                    className="w-full mt-2"
                  >
                    Tutup
                  </Button>
                </div>
              )}

              {/* Info */}
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <h4 className="font-semibold text-sm mb-2">💡 Auto-Failover</h4>
                <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                  <li>• Instance dengan priority lebih rendah akan digunakan pertama kali</li>
                  <li>• Jika instance aktif blocked/disconnected, otomatis failover ke backup</li>
                  <li>• Health check berjalan setiap 5 menit untuk auto-recovery</li>
                  <li>• Anda bisa punya hingga 5 instance untuk high availability</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* NOTIFICATION SETTINGS */}
        <TabsContent value="notifications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Pengaturan Notifikasi</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-semibold">Invoice Baru</h4>
                    <p className="text-sm text-gray-500 dark:text-slate-400">Kirim notifikasi saat invoice dibuat</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={config.enable_invoice_notification}
                    onChange={(e) => setConfig({ 
                      ...config, 
                      enable_invoice_notification: e.target.checked 
                    })}
                    className="w-4 h-4"
                  />
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-semibold">Pengingat Pembayaran</h4>
                    <p className="text-sm text-gray-500 dark:text-slate-400">Kirim reminder sebelum jatuh tempo</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={config.enable_payment_reminder}
                    onChange={(e) => setConfig({ 
                      ...config, 
                      enable_payment_reminder: e.target.checked 
                    })}
                    className="w-4 h-4"
                  />
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-semibold">Konfirmasi Pembayaran</h4>
                    <p className="text-sm text-gray-500 dark:text-slate-400">Kirim konfirmasi setelah pembayaran diterima</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={config.enable_payment_confirmation}
                    onChange={(e) => setConfig({ 
                      ...config, 
                      enable_payment_confirmation: e.target.checked 
                    })}
                    className="w-4 h-4"
                  />
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-semibold">Notifikasi Instalasi</h4>
                    <p className="text-sm text-gray-500 dark:text-slate-400">Kirim selamat datang untuk pelanggan baru</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={config.enable_installation_notification}
                    onChange={(e) => setConfig({ 
                      ...config, 
                      enable_installation_notification: e.target.checked 
                    })}
                    className="w-4 h-4"
                  />
                </div>
              </div>

              <div className="p-4 border rounded-lg space-y-3">
                <h4 className="font-semibold">Jadwal Reminder (hari sebelum jatuh tempo)</h4>
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  Masukkan hari-hari kapan reminder akan dikirim (pisahkan dengan koma)
                </p>
                <div>
                  <Input
                    placeholder="Contoh: 5 atau 7,3,1"
                    value={config.reminder_days.join(', ')}
                    onChange={(e) => {
                      const days = e.target.value
                        .split(',')
                        .map(d => parseInt(d.trim()))
                        .filter(d => !isNaN(d) && d > 0);
                      setConfig({ ...config, reminder_days: days.length > 0 ? days : [5] });
                    }}
                  />
                </div>
                <div className="flex gap-2 flex-wrap">
                  {config.reminder_days.map((day, index) => (
                    <Badge key={index} variant="default">H-{day}</Badge>
                  ))}
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg">
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    💡 <strong>Tips:</strong> Untuk menghemat biaya, gunakan 1 reminder saja (contoh: 5 untuk H-5)
                  </p>
                </div>
              </div>

              {/* Anti-Block Settings */}
              <div className="p-4 border-2 border-orange-500 rounded-lg space-y-4 bg-orange-50 dark:bg-orange-900/20">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-orange-900 dark:text-orange-100">⚠️ Pengaturan Anti-Block WhatsApp</h4>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Atur jeda pengiriman untuk mencegah WhatsApp terblokir karena spam
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Batch Size */}
                  <div>
                    <Label className="text-sm font-semibold">Jumlah Pesan 1x Kirim</Label>
                    <select
                      value={config.message_batch_size}
                      onChange={(e) => setConfig({ ...config, message_batch_size: parseInt(e.target.value) })}
                      className="w-full mt-1 px-3 py-2 border rounded-lg"
                    >
                      <option value="1">1 Pesan</option>
                      <option value="3">3 Pesan</option>
                      <option value="5">5 Pesan</option>
                      <option value="10">10 Pesan</option>
                      <option value="20">20 Pesan</option>
                      <option value="50">50 Pesan</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                      Jumlah pesan yang dikirim sebelum jeda
                    </p>
                  </div>

                  {/* Delay Min */}
                  <div>
                    <Label className="text-sm font-semibold">Jeda Minimum (Menit)</Label>
                    <select
                      value={config.delay_min_seconds}
                      onChange={(e) => setConfig({ ...config, delay_min_seconds: parseInt(e.target.value) })}
                      className="w-full mt-1 px-3 py-2 border rounded-lg"
                    >
                      <option value="60">1 Menit</option>
                      <option value="120">2 Menit</option>
                      <option value="180">3 Menit</option>
                      <option value="240">4 Menit</option>
                      <option value="300">5 Menit</option>
                      <option value="600">10 Menit</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                      Jeda minimum antara batch
                    </p>
                  </div>

                  {/* Delay Max */}
                  <div>
                    <Label className="text-sm font-semibold">Jeda Maksimum (Menit)</Label>
                    <select
                      value={config.delay_max_seconds}
                      onChange={(e) => setConfig({ ...config, delay_max_seconds: parseInt(e.target.value) })}
                      className="w-full mt-1 px-3 py-2 border rounded-lg"
                    >
                      <option value="120">2 Menit</option>
                      <option value="180">3 Menit</option>
                      <option value="240">4 Menit</option>
                      <option value="300">5 Menit</option>
                      <option value="420">7 Menit</option>
                      <option value="600">10 Menit</option>
                      <option value="900">15 Menit</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                      Jeda maksimum antara batch
                    </p>
                  </div>
                </div>

                <div className="bg-yellow-100 dark:bg-yellow-900/30 p-3 rounded-lg border border-yellow-300">
                  <p className="text-xs text-gray-700 dark:text-gray-300">
                    <strong>💡 Cara Kerja:</strong><br/>
                    • Sistem akan kirim <strong>{config.message_batch_size} pesan</strong> sekaligus<br/>
                    • Kemudian <strong>jeda random {Math.floor(config.delay_min_seconds/60)}-{Math.floor(config.delay_max_seconds/60)} menit</strong><br/>
                    • Lanjut batch berikutnya<br/>
                    • <strong>Rekomendasi:</strong> 5 pesan dengan jeda 3-5 menit untuk menghindari spam detection
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TEMPLATE EDITOR */}
        <TabsContent value="templates" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Kustomisasi Template Pesan</CardTitle>
              <p className="text-sm text-gray-500 dark:text-slate-400">
                Edit template pesan WhatsApp yang akan dikirim otomatis
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Common Variables Section */}
              <Card className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Code className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    Common Variables - Variable yang Tersedia
                  </CardTitle>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    Klik tombol &quot;Copy&quot; untuk menyalin variable ke clipboard, lalu paste ke template pesan Anda.
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {[
                      { var: '{customer_name}', desc: 'Nama pelanggan' },
                      { var: '{customer_email}', desc: 'Email pelanggan' },
                      { var: '{customer_id}', desc: 'ID/Nomor pelanggan' },
                      { var: '{customer_phone}', desc: 'Nomor telepon pelanggan' },
                      { var: '{invoice_id}', desc: 'Nomor invoice' },
                      { var: '{invoice_number}', desc: 'Nomor invoice (alternatif)' },
                      { var: '{package_name}', desc: 'Paket langganan pelanggan' },
                      { var: '{payment_type}', desc: 'Tipe pembayaran (Prepaid/Postpaid)' },
                      { var: '{activation_date}', desc: 'Tanggal aktivasi pelanggan' },
                      { var: '{due_date}', desc: 'Tanggal jatuh tempo pelanggan' },
                      { var: '{amount}', desc: 'Jumlah tagihan' },
                      { var: '{total_bill}', desc: 'Total tagihan pelanggan' },
                      { var: '{payment_date}', desc: 'Tanggal pembayaran' },
                      { var: '{payment_method}', desc: 'Metode pembayaran (Cash/Xendit/Duitku)' },
                      { var: '{company_name}', desc: 'Nama perusahaan Anda' },
                      { var: '{emoji}', desc: 'Emoji untuk pengingat (⚠️)' },
                      { var: '{urgency}', desc: 'Tingkat urgensi pembayaran' },
                      { var: '{days_left}', desc: 'Sisa hari sebelum jatuh tempo' },
                    ].map((item, idx) => (
                      <div 
                        key={idx}
                        className="bg-white rounded-lg p-3 border border-blue-100 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer group dark:bg-slate-800"
                        onClick={() => {
                          navigator.clipboard.writeText(item.var);
                          toast.success(`${item.var} disalin!`, { duration: 1500 });
                        }}
                        title="Klik untuk copy"
                      >
                        <div className="flex items-start gap-3">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs shrink-0 group-hover:bg-blue-50 group-hover:border-blue-300"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(item.var);
                              toast.success(`${item.var} disalin!`, { duration: 1500 });
                            }}
                          >
                            Copy
                          </Button>
                          <div className="flex-1 min-w-0">
                            <code className="text-xs font-bold text-blue-600 block break-all dark:text-blue-400">
                              {item.var}
                            </code>
                            <p className="text-[10px] text-gray-600 mt-1 dark:text-slate-300">
                              = {item.desc}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg dark:bg-amber-900/30">
                    <p className="text-xs text-amber-800">
                      <strong>💡 Tips:</strong> Gunakan variable ini di template pesan untuk data yang dinamis. 
                      Variable akan otomatis diganti dengan data aktual saat pesan dikirim.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {templates.map((template, index) => (
                <div key={template.id} className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">{template.template_name}</h4>
                      <p className="text-xs text-gray-500 dark:text-slate-400">
                        Type: {template.template_type}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingTemplateId(editingTemplateId === template.id ? null : template.id)}
                        className="text-indigo-600 hover:bg-indigo-50 border-indigo-200 dark:text-indigo-400 dark:hover:bg-indigo-900/30"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        {editingTemplateId === template.id ? 'Tutup' : 'Edit'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handlePreviewTemplate(template)}
                        className="text-blue-600 hover:bg-blue-50 border-blue-200 dark:text-blue-400 dark:hover:bg-blue-900/30"
                      >
                        <MessageSquare className="w-4 h-4 mr-1" />
                        Preview
                      </Button>
                      <Badge variant={template.is_active ? "default" : "secondary"}>
                        {template.is_active ? "Aktif" : "Nonaktif"}
                      </Badge>
                    </div>
                  </div>

                  {/* Editor Section - Only show when editing */}
                  {editingTemplateId === template.id && (
                    <div className="space-y-4 mt-4">
                      {/* Available Variables with Descriptions */}
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                        <p className="text-sm font-bold text-blue-900 dark:text-blue-100 mb-3 flex items-center gap-2">
                          <span className="text-lg">📝</span> 
                          Variable Yang Tersedia - Klik untuk Copy
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {template.available_variables.map((variable, idx) => {
                            // Define descriptions for each variable
                            const variableDescriptions = {
                              '{company_name}': 'Nama perusahaan/ISP',
                              '{customer_name}': 'Nama pelanggan',
                              '{customer_email}': 'Email pelanggan',
                              '{customer_id}': 'ID/Nomor pelanggan',
                              '{customer_phone}': 'Nomor telepon pelanggan',
                              '{package_name}': 'Nama paket layanan',
                              '{payment_type}': 'Tipe pembayaran (Prepaid/Postpaid)',
                              '{activation_date}': 'Tanggal aktivasi',
                              '{due_date}': 'Tanggal jatuh tempo',
                              '{total_bill}': 'Total tagihan',
                              '{invoice_id}': 'Nomor invoice',
                              '{invoice_number}': 'Nomor invoice',
                              '{amount}': 'Jumlah tagihan',
                              '{payment_date}': 'Tanggal pembayaran',
                              '{payment_method}': 'Metode pembayaran',
                              '{emoji}': 'Emoji (untuk pengingat)',
                              '{urgency}': 'Tingkat urgensi',
                              '{days_left}': 'Sisa hari'
                            };
                            
                            const description = variableDescriptions[variable] || 'Data dinamis';
                            
                            return (
                              <div 
                                key={idx} 
                                className="bg-white dark:bg-slate-800 p-2 rounded border border-blue-100 dark:border-blue-700 hover:border-blue-300 dark:hover:border-blue-500 hover:shadow-sm transition-all cursor-pointer group"
                                onClick={() => {
                                  navigator.clipboard.writeText(variable);
                                  toast.success(`${variable} disalin!`, { duration: 1000 });
                                }}
                                title="Klik untuk copy"
                              >
                                <code className="text-xs font-bold text-blue-600 dark:text-blue-400 block">
                                  {variable}
                                </code>
                                <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                                  {description}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-3 italic">
                          💡 Klik variable untuk copy otomatis ke clipboard
                        </p>
                      </div>

                      {/* Template Editor */}
                      <div>
                        <Label className="text-sm font-semibold">Template Pesan</Label>
                        <textarea
                          className="w-full p-3 border rounded-lg min-h-[300px] font-mono text-sm mt-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          value={template.template_content}
                          onChange={(e) => updateTemplateContent(template.id, e.target.value)}
                          placeholder="Tulis template pesan..."
                        />
                        <p className="text-xs text-gray-500 mt-2 dark:text-slate-400">
                          💡 Tips: Gunakan *text* untuk bold, gunakan variable {'{...}'} untuk data dinamis
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Preview Content - Show when not editing */}
                  {editingTemplateId !== template.id && (
                    <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                      <p className="text-xs text-gray-500 mb-2 font-semibold dark:text-slate-400">Preview:</p>
                      <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-sans">
                        {template.template_content.substring(0, 200)}
                        {template.template_content.length > 200 && '...'}
                      </pre>
                    </div>
                  )}
                </div>
              ))}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4">
                <Button 
                  onClick={handleSaveTemplates} 
                  disabled={loading}
                  className="flex-1"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Menyimpan...' : 'Simpan Semua Template'}
                </Button>
                <Button 
                  onClick={handleResetTemplates} 
                  disabled={loading}
                  variant="outline"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reset ke Default
                </Button>
              </div>

              {/* Info Box */}
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <h4 className="font-semibold text-sm mb-2">💡 Tips Penggunaan Template</h4>
                <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                  <li>• Gunakan variable {'{...}'} untuk data dinamis yang akan otomatis diganti</li>
                  <li>• Format *teks* untuk bold, _teks_ untuk italic</li>
                  <li>• Emoji dapat digunakan untuk membuat pesan lebih menarik</li>
                  <li>• Simpan perubahan untuk menerapkan template baru</li>
                  <li>• Reset ke default jika ingin kembali ke template asli</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* HISTORY & STATISTICS */}
        <TabsContent value="history" className="space-y-6">
          <Card>
            <CardHeader className="pb-4">
              <CardTitle>Statistik Pesan WhatsApp</CardTitle>
              <p className="text-sm text-gray-500 mt-1 dark:text-slate-400">Monitoring pesan yang terkirim untuk memastikan sistem bekerja dengan baik</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Refresh Button */}
              <div className="mb-6">
                <button
                  onClick={() => { fetchStatistics(); fetchMessages(); }}
                  className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold shadow-sm"
                >
                  <RefreshCw className="w-4 h-4 inline mr-2" />
                  Refresh Data
                </button>
              </div>

              {/* Bulk Delete Actions */}
              {!isKepalaCabang && (
              <div className="p-4 border-2 border-red-200 rounded-xl bg-red-50 dark:bg-red-900/10 mb-6">
                <h4 className="text-sm font-semibold text-red-900 dark:text-red-100 mb-3">
                  🗑️ Hapus Pesan Massal (Bulk Delete)
                </h4>
                <div className="flex flex-wrap gap-2 mb-3">
                  <button
                    onClick={() => bulkDeleteMessages(7)}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm font-semibold transition-colors"
                  >
                    Hapus &gt;7 Hari
                  </button>
                  <button
                    onClick={() => bulkDeleteMessages(30)}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-semibold transition-colors"
                  >
                    Hapus &gt;30 Hari
                  </button>
                  <button
                    onClick={() => bulkDeleteMessages(60)}
                    className="px-4 py-2 bg-orange-700 text-white rounded-lg hover:bg-orange-800 text-sm font-semibold transition-colors"
                  >
                    Hapus &gt;60 Hari
                  </button>
                  <button
                    onClick={() => bulkDeleteMessages(90)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-semibold transition-colors"
                  >
                    Hapus &gt;90 Hari
                  </button>
                  <button
                    onClick={() => setShowDeleteAllDialog(true)}
                    className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 text-sm font-semibold transition-colors border-2 border-red-900"
                  >
                    🚨 Hapus SEMUA Pesan
                  </button>
                </div>
                <div className="mt-3 flex flex-wrap items-end gap-3 border-t border-red-200 dark:border-red-800 pt-3" data-testid="bulk-delete-range">
                  <div>
                    <label className="block text-xs font-medium text-red-700 dark:text-red-300 mb-1">Dari Tanggal</label>
                    <input
                      type="date"
                      value={bulkDateFrom}
                      onChange={(e) => setBulkDateFrom(e.target.value)}
                      className="px-3 py-2 border border-red-300 rounded-lg text-sm dark:bg-gray-700 dark:border-red-800"
                      data-testid="bulk-delete-date-from"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-red-700 dark:text-red-300 mb-1">Sampai Tanggal</label>
                    <input
                      type="date"
                      value={bulkDateTo}
                      onChange={(e) => setBulkDateTo(e.target.value)}
                      className="px-3 py-2 border border-red-300 rounded-lg text-sm dark:bg-gray-700 dark:border-red-800"
                      data-testid="bulk-delete-date-to"
                    />
                  </div>
                  <button
                    onClick={bulkDeleteRange}
                    disabled={loading}
                    className="px-4 py-2 bg-red-800 hover:bg-red-900 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
                    data-testid="bulk-delete-range-btn"
                  >
                    🗑️ Hapus Rentang Ini
                  </button>
                </div>

                <p className="text-xs text-gray-600 dark:text-gray-400">
                  💡 Hapus pesan lama secara massal untuk menghemat space database. Filter di bawah akan diterapkan.
                </p>
              </div>
              )}

              {statistics && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  {/* Today Stats */}
                  <div className="p-4 border rounded-lg bg-blue-50 dark:bg-blue-900/20">
                    <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-400">Hari Ini</h4>
                    <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{statistics.today.total}</p>
                    <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                      ✅ {statistics.today.success} berhasil | ❌ {statistics.today.failed} gagal
                    </p>
                    <p className="text-sm font-semibold text-green-600 mt-2 dark:text-green-400">
                      Success Rate: {statistics.today.success_rate}%
                    </p>
                  </div>

                  {/* Month Stats */}
                  <div className="p-4 border rounded-lg bg-green-50 dark:bg-green-900/20">
                    <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-400">Bulan Ini</h4>
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400">{statistics.month.total}</p>
                    <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                      ✅ {statistics.month.success} berhasil | ❌ {statistics.month.failed} gagal
                    </p>
                    <p className="text-sm font-semibold text-green-600 mt-2 dark:text-green-400">
                      Success Rate: {statistics.month.success_rate}%
                    </p>
                  </div>

                  {/* Pending */}
                  <div className="p-4 border rounded-lg bg-yellow-50 dark:bg-yellow-900/20">
                    <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-400">Pending</h4>
                    <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">{statistics.pending}</p>
                    <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">Menunggu dikirim</p>
                  </div>

                  {/* Message Types */}
                  <div className="p-4 border rounded-lg bg-purple-50 dark:bg-purple-900/20">
                    <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">Tipe Pesan (Bulan Ini)</h4>
                    <div className="text-xs space-y-1">
                      <p>🧪 Test: {statistics.message_types.test || 0}</p>
                      <p>📄 Invoice: {statistics.message_types.invoice}</p>
                      <p>⏰ Reminder: {statistics.message_types.reminder}</p>
                      <p>✅ Payment: {statistics.message_types.payment_confirmation}</p>
                      <p>🎉 Registrasi: {(statistics.message_types.installation || 0) + (statistics.message_types.registration || 0)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Filters & Controls */}
              <div className="p-5 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 mb-6 shadow-sm">
                {/* Section Title */}
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Filter & Pengaturan Tampilan</h3>
                
                {/* Grid Layout */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  {/* Search */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Cari</label>
                    <input
                      type="text"
                      placeholder="Nama, nomor, atau pesan..."
                      value={messageFilter.search}
                      onChange={(e) => {
                        const nf = {...messageFilter, search: e.target.value};
                        setMessageFilter(nf);
                        if (waSearchTimer.current) clearTimeout(waSearchTimer.current);
                        waSearchTimer.current = setTimeout(() => fetchMessages(nf), 600);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          fetchMessages();
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      data-testid="wa-history-search-input"
                    />
                  </div>
                  {/* Filter Status */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Status</label>
                    <select
                      value={messageFilter.status}
                      onChange={(e) => { 
                        const nf = {...messageFilter, status: e.target.value};
                        setMessageFilter(nf);
                        fetchMessages(nf);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">Semua Status</option>
                      <option value="sent">Terkirim</option>
                      <option value="delivered">Diterima</option>
                      <option value="read">Dibaca</option>
                      <option value="failed">Gagal</option>
                      <option value="skipped">Dilewati (nomor tidak aktif)</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>

                  {/* Filter Tipe */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Tipe Pesan</label>
                    <select
                      value={messageFilter.message_type}
                      onChange={(e) => { 
                        const nf = {...messageFilter, message_type: e.target.value};
                        setMessageFilter(nf);
                        fetchMessages(nf);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">Semua Tipe</option>
                      <option value="test">Test</option>
                      <option value="invoice">Invoice</option>
                      <option value="reminder">Reminder</option>
                      <option value="payment_confirmation">Konfirmasi Pembayaran</option>
                      <option value="registration">Registrasi</option>
                    </select>
                  </div>

                  {/* Show Entries */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Tampilkan</label>
                    <select
                      value={messageFilter.limit}
                      onChange={(e) => { 
                        const nf = {...messageFilter, limit: parseInt(e.target.value)};
                        setMessageFilter(nf);
                        fetchMessages(nf);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="10">10 entri</option>
                      <option value="25">25 entri</option>
                      <option value="50">50 entri</option>
                      <option value="100">100 entri</option>
                      <option value="200">200 entri</option>
                    </select>
                  </div>

                  {/* Sort By */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Urutkan Berdasarkan</label>
                    <div className="flex gap-2">
                      <select
                        value={messageFilter.sortBy}
                        onChange={(e) => { 
                          const nf = {...messageFilter, sortBy: e.target.value};
                          setMessageFilter(nf);
                          fetchMessages(nf);
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="created_at">Waktu</option>
                        <option value="status">Status</option>
                        <option value="message_type">Tipe</option>
                        <option value="customer_name">Customer</option>
                      </select>
                      <select
                        value={messageFilter.sortOrder}
                        onChange={(e) => { 
                          const nf = {...messageFilter, sortOrder: e.target.value};
                          setMessageFilter(nf);
                          fetchMessages(nf);
                        }}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="desc">↓</option>
                        <option value="asc">↑</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Messages Table */}
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Waktu</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">ID Pelanggan</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Nomor</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Tipe</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {messages.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500 dark:text-slate-400">
                          Belum ada pesan. Klik "Refresh Data" untuk memuat data.
                        </td>
                      </tr>
                    ) : (
                      messages.map((msg) => (
                        <tr key={msg.id} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                          <td className="px-4 py-3 text-xs">
                            {new Date(msg.created_at).toLocaleString('id-ID')}
                          </td>
                          <td className="px-4 py-3 text-xs">{msg.customer_account_number || (msg.customer_id && msg.customer_id !== 'test' ? msg.customer_id : '-')}</td>
                          <td className="px-4 py-3 text-xs">{msg.customer_name || (msg.message_type === 'test' ? 'Test Manual' : '-')}</td>
                          <td className="px-4 py-3 text-xs">{msg.phone_number}</td>
                          <td className="px-4 py-3 text-xs">
                            {msg.message_type === 'test' && '🧪 Test'}
                            {msg.message_type === 'invoice' && '📄 Invoice'}
                            {msg.message_type === 'reminder' && '⏰ Reminder'}
                            {msg.message_type === 'payment_confirmation' && '✅ Payment'}
                            {(msg.message_type === 'installation' || msg.message_type === 'registration') && '🎉 Registrasi'}
                          </td>
                          <td className="px-4 py-3 text-xs">
                            {msg.status === 'sent' && <Badge variant="default">Terkirim</Badge>}
                            {msg.status === 'delivered' && <Badge variant="default" className="bg-green-600" data-testid={`wa-status-delivered-${msg.id}`}>Diterima</Badge>}
                            {msg.status === 'read' && <Badge variant="default" className="bg-sky-600" data-testid={`wa-status-read-${msg.id}`}>Dibaca</Badge>}
                            {msg.status === 'skipped' && <Badge variant="secondary" className="bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200" data-testid={`wa-status-skipped-${msg.id}`}>Dilewati</Badge>}
                            {msg.status === 'failed' && <Badge variant="destructive">Gagal</Badge>}
                            {msg.status === 'pending' && <Badge variant="secondary">Pending</Badge>}
                            {msg.error_message && (
                              <p className="text-xs text-red-600 mt-1 dark:text-red-400">{msg.error_message}</p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs">
                            {(msg.status === 'failed' || msg.status === 'pending' || msg.status === 'skipped') && (
                              <button
                                onClick={() => resendMessage(msg.id)}
                                className="text-blue-600 hover:text-blue-800 font-semibold transition-colors dark:text-blue-400 mr-3"
                                title="Kirim ulang pesan ini"
                                data-testid={`wa-resend-${msg.id}`}
                              >
                                🔄 Kirim Ulang
                              </button>
                            )}
                            {!isKepalaCabang && (
                            <button
                              onClick={() => deleteMessage(msg.id)}
                              className="text-red-600 hover:text-red-800 font-semibold transition-colors dark:text-red-400"
                              title="Hapus pesan"
                            >
                              🗑️ Hapus
                            </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {allMessages.length > 0 && (
                <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  {/* Info */}
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Menampilkan <span className="font-semibold text-gray-900 dark:text-gray-100">{startEntry}</span> sampai <span className="font-semibold text-gray-900 dark:text-gray-100">{endEntry}</span> dari <span className="font-semibold text-gray-900 dark:text-gray-100">{totalEntries}</span> entri
                  </div>

                  {/* Pagination Buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`px-5 py-2.5 border rounded-lg text-sm font-semibold transition-colors shadow-sm ${
                        currentPage === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                          : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                      }`}
                    >
                      ← Previous
                    </button>

                    <div className="flex items-center gap-2 px-4">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Halaman <span className="font-semibold text-gray-900 dark:text-gray-100">{currentPage}</span> dari <span className="font-semibold text-gray-900 dark:text-gray-100">{totalPages}</span>
                      </span>
                    </div>

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`px-5 py-2.5 border rounded-lg text-sm font-semibold transition-colors shadow-sm ${
                        currentPage === totalPages
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                          : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                      }`}
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TEST MESSAGE */}
        <TabsContent value="test" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Kirim Pesan Test</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Nomor WhatsApp</Label>
                <Input
                  placeholder="628123456789"
                  value={testMessage.phone_number}
                  onChange={(e) => setTestMessage({ 
                    ...testMessage, 
                    phone_number: e.target.value 
                  })}
                />
                <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">
                  Format: 628xxx (dengan kode negara, tanpa +)
                </p>
              </div>

              <div>
                <Label>Pesan</Label>
                <textarea
                  className="w-full p-2 border rounded-lg min-h-[100px]"
                  placeholder="Tulis pesan test..."
                  value={testMessage.message}
                  onChange={(e) => setTestMessage({ 
                    ...testMessage, 
                    message: e.target.value 
                  })}
                />
              </div>

              <Button 
                onClick={handleSendTestMessage}
                disabled={loading}
                className="w-full"
              >
                <Send className="w-4 h-4 mr-2" />
                {loading ? 'Mengirim...' : 'Kirim Pesan Test'}
              </Button>

              {status && (
                <div className={`p-4 rounded-lg ${
                  status.connected 
                    ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                    : 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
                }`}>
                  <div className="flex items-start gap-2">
                    {status.connected ? (
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 dark:text-green-400" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 dark:text-yellow-400" />
                    )}
                    <div className="flex-1">
                      <h4 className="font-semibold text-sm">
                        {status.connected ? 'WhatsApp Connected' : 'WhatsApp Not Connected'}
                      </h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        Provider: {status.provider || 'Not configured'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Save Button */}
      <div className="flex justify-end gap-2">
        <Button onClick={fetchStatus} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh Status
        </Button>
        <Button onClick={handleSaveConfig} disabled={loading}>
          <Save className="w-4 h-4 mr-2" />
          {loading ? 'Menyimpan...' : 'Simpan Konfigurasi'}
        </Button>
      </div>

      {/* Delete ALL Messages Confirmation Dialog */}
      <Dialog open={showDeleteAllDialog} onOpenChange={setShowDeleteAllDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-700 flex items-center gap-2 dark:text-red-300">
              <Trash2 className="w-6 h-6" />
              🚨 HAPUS SEMUA PESAN WhatsApp
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-red-100 border-2 border-red-300 rounded-lg p-4 dark:bg-red-900/40">
              <p className="text-sm text-red-900 font-bold">
                ⚠️⚠️⚠️ PERINGATAN KERAS ⚠️⚠️⚠️
              </p>
              <p className="text-sm text-red-800 mt-2">
                Tindakan ini akan menghapus <span className="font-bold text-lg">SEMUA PESAN WhatsApp</span> di database!
              </p>
              <p className="text-sm text-red-800 mt-2 font-semibold">
                Termasuk history: Registrasi, Invoice, Payment, Reminder, Test messages
              </p>
              <p className="text-sm text-red-800 mt-2 font-semibold">
                Data tidak dapat dikembalikan setelah dihapus!
              </p>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-slate-700 font-medium dark:text-slate-200">
                Ketik <span className="font-bold text-red-700 text-lg dark:text-red-300">HAPUS SEMUA</span> untuk mengkonfirmasi:
              </p>
              <Input
                type="text"
                value={deleteAllConfirmText}
                onChange={(e) => setDeleteAllConfirmText(e.target.value)}
                placeholder="Ketik HAPUS SEMUA"
                className="border-red-400 focus:border-red-600 focus:ring-red-600 font-mono"
              />
              <p className="text-xs text-red-600 italic dark:text-red-400">
                * Harus sama persis: HAPUS SEMUA (huruf besar semua)
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteAllDialog(false);
                  setDeleteAllConfirmText('');
                }}
              >
                Batal
              </Button>
              <Button
                variant="destructive"
                disabled={deleteAllConfirmText !== 'HAPUS SEMUA'}
                onClick={handleDeleteAllMessages}
                className="bg-red-700 hover:bg-red-900"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Hapus SEMUA Pesan
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
              <MessageSquare className="w-5 h-5" />
              Preview Template
            </DialogTitle>
          </DialogHeader>
          {previewTemplate && (
            <div className="space-y-4">
              {/* Template Info */}
              <div className="bg-slate-50 rounded-lg p-3 dark:bg-slate-900">
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {previewTemplate.template_name}
                </div>
                <div className="text-xs text-slate-500 mt-1 dark:text-slate-400">
                  Type: {previewTemplate.template_type}
                </div>
              </div>

              {/* WhatsApp Style Preview */}
              <div className="bg-[#e5ddd5] p-4 rounded-lg">
                <div className="bg-white rounded-lg p-3 shadow-sm max-w-full dark:bg-slate-800">
                  {/* Message Content */}
                  <div 
                    className="text-sm text-slate-900 whitespace-pre-wrap break-words dark:text-slate-100"
                    style={{ fontFamily: 'system-ui, -apple-system' }}
                  >
                    {renderPreviewWithSampleData(
                      previewTemplate.template_content,
                      previewTemplate.template_type
                    ).split('\n').map((line, idx) => {
                      // Check if line has bold markers (*)
                      if (line.includes('*')) {
                        // Split by * and render bold parts
                        const parts = line.split('*');
                        return (
                          <div key={idx}>
                            {parts.map((part, pIdx) => 
                              pIdx % 2 === 1 ? <strong key={pIdx}>{part}</strong> : part
                            )}
                          </div>
                        );
                      }
                      return <div key={idx}>{line || '\u00A0'}</div>;
                    })}
                  </div>
                  {/* Message Timestamp */}
                  <div className="flex items-center justify-end gap-1 mt-2">
                    <span className="text-[10px] text-slate-400 dark:text-slate-500">11:22</span>
                    <Check className="w-3 h-3 text-blue-500" />
                  </div>
                </div>
              </div>

              {/* Info Note */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 dark:bg-blue-900/30">
                <p className="text-xs text-blue-800">
                  <strong>💡 Catatan:</strong> Ini adalah preview dengan data sample. 
                  Data aktual akan diganti otomatis saat pesan dikirim.
                </p>
              </div>

              {/* Close Button */}
              <div className="flex justify-end pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowPreviewDialog(false)}
                >
                  Tutup
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WhatsAppSettings;
